import { matchPath } from "react-router-dom";
import type { Params } from "react-router-dom";
import { urls } from "../../urls";
import type { WireMessage } from "./agentMemory";
import type { ConversationSubject } from "./conversationSubject";
import type { ShownCreationArea, WizardStep, WizardStepField, WizardStepLayout } from "./pageEntityNames";
import {
  BASIS_CONTRACT_PARAM,
  BASIS_PROCUREMENT_PARAM,
  CREATES_PROCUREMENT_PARAM,
  CREATES_PROCUREMENT_VALUE,
  NEED_PARAM,
  PROCUREMENT_PARAM,
  PROJECT_PARAM,
} from "./createProcurementParams";
import { PERIOD_FIELDS_NOTE } from "./wizardHiddenFields";

export type PageEntityKind = "contract" | "v2Event" | "project" | "need" | "organization";

export type PageEntity = { kind: PageEntityKind; id: string; step?: { key: string; label?: string } };

export type PageContext = { description: string; entity?: PageEntity };

export const PAGE_CONTEXT_PREFIX = "Brukeren ser nå på:";

type Lookups = {
  entityLabel: (id: string) => string | undefined;
  wizardStep?: (eventId: string) => WizardStep | undefined;
  creationProgress?: () => ShownCreationArea[] | undefined;
  withholdsPeriodFields?: () => boolean;
};

type EntityKind = PageEntityKind;

const ENTITY_PHRASES: Record<EntityKind, { noun: string; referenceClause: string; pronoun: string }> = {
  contract: { noun: "Kontrakt", referenceClause: "«denne kontrakten»", pronoun: "denne" },
  v2Event: { noun: "Anskaffelse", referenceClause: "«denne anskaffelsen»", pronoun: "denne" },
  project: { noun: "Prosjekt", referenceClause: "«dette prosjektet»", pronoun: "dette" },
  need: { noun: "Behov", referenceClause: "«dette behovet»", pronoun: "dette" },
  organization: { noun: "Bedrift", referenceClause: "«denne bedriften» eller «dette selskapet»", pronoun: "denne" },
};

const entityContext = (kind: EntityKind, id: string, lookups: Lookups, idLabel = "id"): PageContext => {
  const { noun, referenceClause, pronoun } = ENTITY_PHRASES[kind];
  const label = lookups.entityLabel(id);
  const subject = label ? `${noun} «${label}» (${idLabel} ${id})` : `${noun} med ${idLabel} ${id}`;
  return {
    description:
      `${PAGE_CONTEXT_PREFIX} ${subject}. Når brukeren sier ${referenceClause}, er det ${pronoun}. ` +
      "Det gjelder også når samtalen tidligere handlet om noe annet: hent data om nettopp denne id-en " +
      "i stedet for å svare ut fra det som ble sagt tidligere.",
    entity: { kind, id },
  };
};

const pageOnlyContext = (pageLabel: string): PageContext => ({
  description:
    `${PAGE_CONTEXT_PREFIX} ${pageLabel}. Det er ingen bestemt kontrakt, anskaffelse eller prosjekt på denne siden. ` +
    "Si aldri at brukeren ser på noe som ble nevnt tidligere i samtalen. Mener brukeren «denne …», si hvilken " +
    "du tror det gjelder ut fra samtalen («den vi snakket om»), eller spør.",
});

type Matcher = { pattern: string; resolve: (params: Params, search: string, lookups: Lookups) => PageContext | null };

const entity = (pattern: string, kind: EntityKind, idParam: string): Matcher => ({
  pattern,
  resolve: (params, _search, lookups) => {
    const id = params[idParam];
    return id ? entityContext(kind, id, lookups) : null;
  },
});

const entityFromQuery = (pattern: string, kind: EntityKind, idParams: string[]): Matcher => ({
  pattern,
  resolve: (_params, search, lookups) => {
    const query = new URLSearchParams(search);
    const idParam = idParams.find((param) => query.get(param));
    const id = idParam ? query.get(idParam) : null;
    return id && idParam ? entityContext(kind, id, lookups, idParam) : null;
  },
});

const procurementCreation = (pattern: string): Matcher => ({
  pattern,
  resolve: (_params, search, lookups) => {
    const query = new URLSearchParams(search);
    if (query.get(CREATES_PROCUREMENT_PARAM) !== CREATES_PROCUREMENT_VALUE) return null;
    const named = (id: string, idLabel: string) => {
      const name = lookups.entityLabel(id);
      return name ? `«${name}» (${idLabel} ${id})` : `med ${idLabel} ${id}`;
    };
    const projectId = query.get(PROJECT_PARAM);
    const needId = query.get(NEED_PARAM);
    const needProcurementId = query.get(PROCUREMENT_PARAM);
    const basisProcurementId = query.get(BASIS_PROCUREMENT_PARAM);
    const basisContractId = query.get(BASIS_CONTRACT_PARAM);
    const parts = [
      `${PAGE_CONTEXT_PREFIX} «Opprett med KI», der brukeren oppretter en ny anskaffelse sammen med deg.`,
      projectId &&
        `Anskaffelsen skal høre til prosjektet ${named(projectId, "id")}: systemet knytter den til prosjektet når ` +
          "du kaller createSourcingEvent. Finnes anskaffelsen allerede, send prosjekt-id-en i eventData.projects med " +
          "updateSourcingEvent.",
      needId &&
        needProcurementId &&
        `Anskaffelsen er allerede opprettet fra behovet ${named(needId, "behov-id")}, og har id ${needProcurementId}. ` +
          "Opprett ingen ny: hent behovet med getNeed og anskaffelsen med getSourcingEvent, og fyll ut anskaffelsen " +
          `med updateSourcingEvent. Oppgi kilde NEED med needId ${needId} for det du henter fra behovet.`,
      !needId &&
        needProcurementId &&
        `Anskaffelsen som bygges her, er allerede opprettet og har id ${needProcurementId}. Opprett ingen ny: fyll ` +
          "ut denne med updateSourcingEvent.",
      basisProcurementId &&
        `Brukeren vil bruke den tidligere anskaffelsen ${named(basisProcurementId, "id")} som utgangspunkt: hent ` +
          "den med getSourcingEvent, bruk innholdet når du oppretter og fyller ut den nye, og oppgi kilde " +
          `EARLIER_PROCUREMENT med sourcingEventId ${basisProcurementId} for det du henter derfra.`,
      basisContractId &&
        `Brukeren vil bruke kontrakten ${named(basisContractId, "contractId")} som utgangspunkt: hent den med ` +
          "getContract, bruk innholdet når du oppretter og fyller ut anskaffelsen, og oppgi kilde EARLIER_CONTRACT " +
          `med contractId ${basisContractId} for det du henter derfra.`,
      progressNote(lookups.creationProgress?.(), lookups.withholdsPeriodFields?.() ?? false),
      "Anskaffelsen lagres som utkast først når brukeren har bekreftet regelverk og prosedyre, så si aldri at den " +
        "er lagret før det. Når alle områdene er fylt ut, åpner skjermen oppsummeringen i skjemaet, med deg ved siden av.",
      "Nevn aldri id-er for brukeren.",
    ];
    return { description: parts.filter(Boolean).join(" ") };
  },
});

const wizard = (pattern: string): Matcher => ({
  pattern,
  resolve: (params, search, lookups) => {
    const id = params.eventId;
    if (!id) return null;
    const context = entityContext("v2Event", id, lookups);
    const remembered = lookups.wizardStep?.(id);
    const key = new URLSearchParams(search).get("step") ?? remembered?.key;
    if (!key) return context;
    const label = remembered?.key === key ? remembered.label : undefined;
    return {
      description:
        `${context.description} Brukeren fyller ut anskaffelsen i veiviseren og står på steget «${label ?? key}». ` +
        "Når brukeren sier «dette steget», er det dette steget." +
        wizardLayoutDescription(key, remembered?.steps),
      entity: { kind: "v2Event", id, step: { key, label } },
    };
  },
});

const namedField = (field: WizardStepField): string =>
  field.paths.length ? `«${field.label}» (${field.paths.join(", ")})` : `«${field.label}»`;

const namedFields = (fields: readonly WizardStepField[]): string => fields.map(namedField).join(", ");

export const wizardLayoutDescription = (currentKey: string, steps: readonly WizardStepLayout[] | undefined): string => {
  const current = steps?.find((step) => step.key === currentKey);
  if (!steps || !current) return "";
  const others = steps.filter((step) => step !== current && step.fields.length > 0);
  const onThisStep = current.fields.length
    ? ` Steget «${current.label}» viser disse feltene: ${namedFields(current.fields)}. Hjelper du med dette steget, ` +
      "foreslår du innhold i alle disse feltene, tomme som utfylte, og ikke i noe annet felt."
    : ` Steget «${current.label}» har ingen felter å fylle ut.`;
  const elsewhere = others.length
    ? ` Feltene på de andre stegene i veiviseren: ${others
        .map((step) => `«${step.label}»: ${namedFields(step.fields)}`)
        .join("; ")}.`
    : "";
  const stepNames = steps.map((step) => `«${step.label}»`).join(", ");
  return (
    onThisStep +
    elsewhere +
    ` Veiviseren har stegene ${stepNames}. Sier du hvilket steg et felt står på, eller hva som er neste steg, ` +
    "bruker du disse navnene og ingen andre."
  );
};

export const conversationSubjectContext = (subject: ConversationSubject, lookups: Lookups): PageContext => {
  const { noun, referenceClause, pronoun } = ENTITY_PHRASES[subject.kind];
  const label = lookups.entityLabel(subject.id) ?? subject.name;
  const named = label ? `${noun} «${label}» (id ${subject.id})` : `${noun} med id ${subject.id}`;
  const step = subject.step ? `, steget «${subject.step.label ?? subject.step.key}» i veiviseren` : "";
  return {
    description:
      `${PAGE_CONTEXT_PREFIX} en annen side enn den denne samtalen ble startet fra. Samtalen gjelder ${named}${step}. ` +
      `Når brukeren sier ${referenceClause}${subject.step ? " eller «dette steget»" : ""}, eller velger et av ` +
      `svaralternativene dine, er det ${pronoun}: bruk denne id-en, ikke siden brukeren har gått til.`,
    entity: { kind: subject.kind, id: subject.id, ...(subject.step ? { step: subject.step } : {}) },
  };
};

const page = (pattern: string, pageLabel: string): Matcher => ({
  pattern,
  resolve: () => pageOnlyContext(pageLabel),
});

const MATCHERS: Matcher[] = [
  procurementCreation(urls.app.assistant),
  page(urls.home, "forsiden"),
  page(urls.app.home, "forsiden"),
  page(urls.contracts.viewAll, "kontraktsoversikten"),
  page(urls.contracts.viewDrafts, "kontraktsoversikten (utkast)"),
  page(urls.contracts.viewPublished, "kontraktsoversikten (publiserte)"),
  page(urls.contracts.search, "kontraktsøket"),
  page(urls.contracts.viewTasks, "oppgaveoversikten for kontrakter"),
  page(urls.contracts.settings, "kontraktsinnstillingene"),
  page(urls.myProfile.organization.settings, "innstillingene"),
  page(urls.app.settings, "innstillingene"),
  page(urls.app.contracts.viewAll, "kontraktsoversikten"),
  page(urls.app.contracts.active, "kontraktsoversikten (aktive)"),
  page(urls.app.contracts.tasks, "oppgaveoversikten for kontrakter"),
  page(urls.app.sourcingEvents.viewAll, "oversikten over anskaffelser"),
  page(urls.projects.viewAll, "prosjektoversikten"),
  page(urls.app.projects.viewAll, "prosjektoversikten"),
  page(urls.app.needs.list, "behovslista"),
  page(urls.app.organizations, "bedriftsoversikten"),
  entityFromQuery(`${urls.app.organizationProfile.pathname}/:section`, "organization", ["entryId", "orgId"]),
  entityFromQuery(`${urls.organizations.profile.pathname}/:section`, "organization", ["entryId", "orgId"]),
  entity(urls.contracts.viewSingle.pathname, "contract", "contractId"),
  entity(urls.contracts.edit.paths.details.pathname, "contract", "contractId"),
  entity(urls.app.contracts.view.pathname, "contract", "contractId"),
  entity(urls.app.contracts.edit.pathname, "contract", "contractId"),
  entity(urls.app.sourcingEvents.view.pathname, "v2Event", "eventId"),
  entity(urls.app.sourcingEvents.protocol.pathname, "v2Event", "eventId"),
  entity(urls.app.sourcingEvents.bidDetail.pathname, "v2Event", "eventId"),
  entity(urls.app.sourcingEvents.participationRequestDetail.pathname, "v2Event", "eventId"),
  wizard(urls.app.sourcingEvents.edit.paths.info.pathname),
  entity(urls.app.sourcingEvents.editNegotiationRound.pathname, "v2Event", "eventId"),
  entity(urls.projects.view.pathname, "project", "projectId"),
  entity(urls.app.projects.view.pathname, "project", "projectId"),
  entity(urls.app.needs.view.pathname, "need", "needId"),
  entity(urls.app.needs.edit.pathname, "need", "needId"),
];

export const resolvePageContext = (pathname: string, search: string, lookups: Lookups): PageContext | null => {
  for (const { pattern, resolve } of MATCHERS) {
    const match = matchPath(pattern, pathname);
    if (match) {
      return resolve(match.params, search, lookups);
    }
  }
  return null;
};

export const withPageContext = (messages: WireMessage[], context: PageContext | null): WireMessage[] => {
  if (!context) return messages;
  const latestQuestion = messages.map((message) => message.role).lastIndexOf("user");
  const note = `[${context.description}]`;
  if (latestQuestion < 0) return [...messages, { role: "user", content: note }];
  const question = messages[latestQuestion];
  return messages.map((message, index) =>
    index === latestQuestion ? { ...question, content: `${note}\n\n${question.content ?? ""}` } : message,
  );
};

const progressNote = (areas: ShownCreationArea[] | undefined, withholdsPeriodFields: boolean): string | null => {
  if (!areas?.length) return null;
  const row = areas.map(({ label, status }) => `«${label}»: ${status}`).join(", ");
  const remaining = areas.filter((area) => area.remaining).map(({ label }) => `«${label}»`);
  const next = remaining[0];
  return (
    `Fremdriftsraden over samtalen viser nå, i denne rekkefølgen: ${row}. Raden er fasiten for hva som gjenstår. ` +
    (next
      ? `Neste område du tar opp, og det du anbefaler i valgblokken, er ${next}, det første som gjenstår i raden. ` +
        `Når du sier hva som gjenstår, nevner du hvert av disse områdene med radens navn, og ingen andre: ${remaining.join(", ")}. ` +
        "Si aldri at et område er ferdig så lenge raden viser at det gjenstår, og aldri at noe gjenstår i et område raden viser som ferdig."
      : "Raden viser ingenting som gjenstår, så si aldri at noe av områdene gjenstår.") +
    (withholdsPeriodFields ? ` ${PERIOD_FIELDS_NOTE}` : "")
  );
};
